import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import { ISubTopic } from '../SubTopicSlice';
import { IBaseUser } from '../UserSlice';
import {
  adminGetQuestionList,
  adminLoadMoreQuestionList,
  adminCreateNewQuestion,
  adminUpdateQuestion,
  adminApproveQuestion,
  adminDeleteQuestion,
  adminGetQuestionById,
  adminGetQuestions,
  adminLoadMoreQuestions,
} from './QuestionAdminApi';
import getQuestionsBySubTopic from './QuestionApi';

export type TQuestionDifficulty = 'EASY' | 'MEDIUM' | 'HARD';

export interface IQuestionTag {
  _id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

export interface IQuestionDetailAnswer {
  _id?: string;
  order: number;
  isCorrect?: boolean;
  content: string;
}

export interface IQuestionSubTopic {
  subTopic: ISubTopic;
  paper: string;
}

export interface IQuestion {
  _id: string;
  subTopics: IQuestionSubTopic[];
  difficulty: TQuestionDifficulty;
  source?: string;
  marks: number;
  tags: Array<string>;
  details: string;
  markingSchema: string;
  answers: Array<IQuestionDetailAnswer>;
  isApproved: boolean;
  approvedBy?: IBaseUser;
  createdAt: string;
  createdBy: IBaseUser;
  deletedAt?: string;
  updatedAt?: string;
  updatedBy?: IBaseUser;
  openEnded?: { question: string };
}

export interface IQuestionState {
  status: 'idle' | 'loading' | 'rejected';
  list: Array<IQuestion>;
  question?: IQuestion;
  count: number;
}

const initialState: IQuestionState = {
  status: 'idle',
  list: [],
  count: 0,
  question: undefined,
};

export const QuestionSlice = createSlice({
  name: 'question',
  initialState,
  reducers: {
    resetQuestion: (state) => {
      state.question = undefined;
    },
    resetQuestionList: (state) => {
      state.list = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ADMIN QUESTION LIST
      .addCase(adminGetQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload.docs;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminGetQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN LOAD MORE
      .addCase(adminLoadMoreQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin Load More Questions
      .addCase(adminLoadMoreQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuestions.fulfilled, (state, action) => {
        state.status = 'idle';

        const newList = [...state.list, ...action.payload.docs];

        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuestions.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN GET QUESTIONS BY ID
      .addCase(adminGetQuestionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(adminGetQuestionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN CREATE NEW QUESTION
      .addCase(adminCreateNewQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateNewQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count += 1;
        const newList = [...state.list, action.payload];
        state.list = newList;
      })
      .addCase(adminCreateNewQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN UPDATE QUESTION
      .addCase(adminUpdateQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminUpdateQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list;

        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index > -1) {
          newList[index] = action.payload;
        }

        state.list = newList;

        if (
          state.question &&
          action.payload &&
          state.question._id === action.payload._id
        ) {
          state.question = {
            ...state.question,
            ...action.payload,
          };
        }
      })
      .addCase(adminUpdateQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN APPROVE QUESTION
      .addCase(adminApproveQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminApproveQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list;
        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );
        if (index > -1) {
          newList[index] = {
            ...newList[index],
            isApproved: action.payload.isApproved,
            approvedBy: action.payload.approvedBy,
          };
        }
        state.list = newList;
      })
      .addCase(adminApproveQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN DELETE QUESTION
      .addCase(adminDeleteQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminDeleteQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list.filter(
          (question) => question._id !== action.payload._id
        );
        state.list = newList;
        state.count -= 1;
      })
      .addCase(adminDeleteQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin Get Questions
      .addCase(adminGetQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestions.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload.docs;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminGetQuestions.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Questions by SubTopic
      .addCase(getQuestionsBySubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuestionsBySubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getQuestionsBySubTopic.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const { resetQuestion, resetQuestionList } = QuestionSlice.actions;

export const getQuestionState = (state: RootState): RootState['question'] =>
  state.question;

export * from './QuestionAdminApi';
export default QuestionSlice.reducer;
